import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { genericMemo, mergeRefs, useIsScrolling } from '@direct-frontend/stdlib';
import { isDesktop } from '@direct-frontend/platform-code';
import { chain } from 'react-aria';

import { Overlay } from '../Overlay';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useVStack } from '../../hooks/useStack';
import { useVisualViewportScrollLock } from '../../hooks/useVisualViewportScrollLock/useVisualViewportScrollLock';
import { LayerFocusScope } from '../../lib/layerManager/components/LayerFocusScope/LayerFocusScope';
import { OverlayContentLogProvider } from '../Overlay/Overlay';
import { useIsFullyScrolled } from '../../hooks/useIsFullyScrolled/useIsFullyScrolled';

import { cls, useDrawerDrag } from './utils';
import { ANIMATION_STATES, DrawerProps } from './types';

import './Drawer.css';

const scrollClass = getScrollbarClassName();

/**
 * Компонент для создания мобильных "шторок"
 */
export const Drawer = genericMemo(
    forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
        {
            header,
            footer,
            color = 'gray',
            children,
            className,
            direction = 'bottom',
            isOpen = false,
            onClose,
            dismissOnOutsideClick = true,
            isDraggable = true,
            mode = 'drawer',
            maxSize,
            isFullscreen,
            dismissOnBack,
            logNode,
            'data-testid': dataTestId,
            isLoading,
            priority,
            onAnimationEnd,
            contentRef: customContentRef,
            containerClassName,
            overlayClassName,
        },
        forwardedRef,
    ) {
        const containerRef = useRef<HTMLDivElement | null>(null);
        const headerRef = useRef<HTMLDivElement>(null);
        const contentRef = useRef<HTMLDivElement>(null);
        const overlayRef = useRef<HTMLDivElement | null>(null);

        const { isScrolling, onScroll } = useIsScrolling();
        const { onScroll: fullyScrolledHandler, ref: fullyScrolledRef, isFullyScrolled } = useIsFullyScrolled();
        const scrollHandler = useMemo(() => chain(onScroll, fullyScrolledHandler), [fullyScrolledHandler, onScroll]);

        const containerRefCallback = useCallback(
            (node: HTMLDivElement | null) => {
                containerRef.current = node;

                if (!forwardedRef) {
                    return;
                }

                if (typeof forwardedRef === 'function') {
                    forwardedRef(node);
                } else {
                    forwardedRef.current = node;
                }
            },
            [forwardedRef],
        );

        // управление анимациями открытия/закрытия
        const [animationState, setAnimationState] = useState(isOpen ? ANIMATION_STATES.OPEN : ANIMATION_STATES.CLOSE);

        const handleAnimationEnd = useCallback<React.AnimationEventHandler<HTMLElement>>(
            event => {
                // колбек следит за всеми дочерними элементами, поэтому проверяем,
                // что сработало именно на корневом элементе
                // Проверяем на оверлее, так как при анмаунте может не сработать на дочернем контейнере.
                if (event.target !== overlayRef.current) {
                    return;
                }

                onAnimationEnd?.(animationState);
            },
            [animationState, onAnimationEnd],
        );

        if (isOpen) {
            if (animationState === ANIMATION_STATES.CLOSE) {
                setAnimationState(ANIMATION_STATES.OPEN);
            }
        } else if (animationState !== ANIMATION_STATES.CLOSE) {
            setAnimationState(ANIMATION_STATES.CLOSE);
        }

        // перемещение шторки
        const { dragProps } = useDrawerDrag({
            isOpen,
            isDraggable,
            containerRef,
            contentRef: customContentRef ?? contentRef,
            direction,
            onClose: onClose,
            maxSize,
            isFullscreen,
        });
        const containerStackClasses = useVStack({});

        useVisualViewportScrollLock({ enabled: isOpen, ref: containerRef });

        const autoFocusProps = useMemo(
            () => ({
                contain: true,
                restoreFocus: true,
                autoFocus: true,
            }),
            [],
        );

        return (
            <Overlay
                data-testid={dataTestId ? `${dataTestId}.Overlay` : undefined}
                innerContentRef={customContentRef ?? contentRef}
                className={cls('overlay', [overlayClassName])}
                isOpen={isOpen}
                onClose={onClose}
                dismissOnOutsideClick={dismissOnOutsideClick}
                dismissOnBack={dismissOnBack}
                priority={priority}
                // Проверяем на оверлее, так как при анмаунте может не сработать на дочернем контейнере
                onAnimationEnd={handleAnimationEnd}
                {...dragProps}
                ref={overlayRef}
            >
                <OverlayContentLogProvider logNode={logNode} componentName="Drawer">
                    <div className={cls('wrapper')}>
                        <LayerFocusScope autoFocusProps={autoFocusProps} isOpen={isOpen}>
                            <div
                                ref={containerRefCallback}
                                className={cls(
                                    {
                                        direction,
                                        mode,
                                        color,
                                        state: animationState,
                                        fullscreen: isFullscreen,
                                        withHeader: Boolean(header),
                                        withFooter: Boolean(footer),
                                    },
                                    [containerStackClasses],
                                )}
                                data-testid={dataTestId}
                                tabIndex={-1}
                                focus-fallback="true"
                            >
                                <div className={cls('container', [containerStackClasses, containerClassName])}>
                                    {header && (
                                        <div
                                            ref={headerRef}
                                            className={cls('header', {
                                                showDivider: isScrolling,
                                                loading: isLoading,
                                            })}
                                        >
                                            {header}
                                        </div>
                                    )}
                                    <div
                                        ref={mergeRefs(customContentRef ? null : contentRef, fullyScrolledRef)}
                                        className={cls('content', { mode, loading: isLoading }, [
                                            className,
                                            isDesktop() ? scrollClass : undefined,
                                        ])}
                                        onScroll={scrollHandler}
                                        data-loading-indicator={isLoading || undefined}
                                    >
                                        {children}
                                    </div>
                                    {footer && (
                                        <div
                                            className={cls('footer', {
                                                loading: isLoading,
                                                showDivider: !isFullyScrolled,
                                            })}
                                        >
                                            {footer}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </LayerFocusScope>
                    </div>
                </OverlayContentLogProvider>
            </Overlay>
        );
    }),
);
